import { each, find, isEmpty, isObject, isUndefined, some, tail, values } from "lodash";

import Sentry from "@similarweb/sentry-logger";

import categoryService from "app/common/services/categoryService";
import { swSettings } from "app/common/services/swSettings";
import { parseKeywordListId } from "app/modules/demand-analysis/keyword-lists";
import { KEYWORD_GROUP_PREFIX, keywordGroupsQueryClient } from "app/services/keyword-groups";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import type {
    IStateObject,
    StateObjectType,
    PageId,
    AppStoreStr,
    IBaseDataObject,
    IFavoriteObject,
    IRecentObject,
} from "app/@types/userdata";
import { AppStore } from "app/@types/userdata";

const HASH_DELIMITER = "_";
const PAGE_ID_DEFAULTS = {
    websiteDefaultPageId: [
        {
            pageId: {
                section: "website",
                subSection: "worldwideOverview",
                subSubSection: null,
            },
            pageName: "analysis.worldwideoverview.title",
        },
    ],
    appDefaultPageId: [
        {
            pageId: {
                section: "apps",
                subSection: "overview",
                subSubSection: "performance",
            },
            pageName: "app.performance.title",
        },
    ],
};

const defaultGlobalState: Record<string, IStateObject> = {};

const persistentProperties: Partial<Record<StateObjectType, { serialize: string[]; deSerialize: string[] }>> = {
    website: {
        serialize: ["type", "mainItem", "comparedItems", "duration", "comparedDuration", "country", "pageId"],
        deSerialize: ["key", "isWWW", "country", "duration"],
    },
    virtual: {
        serialize: ["type", "mainItem", "comparedItems", "duration", "country", "pageId"],
        deSerialize: ["key", "isWWW", "country", "duration"],
    },
    advertiser: {
        serialize: ["type", "mainItem", "comparedItems", "duration", "country", "pageId"],
        deSerialize: ["key", "country", "duration"],
    },
    app: {
        serialize: ["type", "mainItem", "comparedItems", "appStore", "duration", "country", "pageId"],
        deSerialize: ["appId", "country", "duration"],
    },
    keyword: {
        serialize: [
            "type",
            "mainItem",
            "duration",
            "country",
            "pageId",
            "keyword",
            "searchTerm",
            "keywordGroup",
            "topic",
        ],
        deSerialize: ["duration", "country", "keyword", "searchTerm", "keywordGroup", "topic"],
    },
    industry: {
        serialize: ["type", "mainItem", "duration", "country", "pageId", "category", "webSource"],
        deSerialize: ["country", "duration", "category", "webSource"],
    },
    appCategory: {
        serialize: ["type", "store", "country", "country", "category", "device", "mode", "pageId"],
        deSerialize: ["store", "country", "country", "category", "device", "mode"],
    },
    conversionIndustry: {
        serialize: ["type", "mainItem", "duration", "country", "pageId", "industry", "category"],
        deSerialize: ["country", "duration", "industry", "category"],
    },
};

/**
 * Generate a hash from an object
 * @param obj Object
 * @returns String
 */
function getObjectHash(obj: FixmeType): string {
    if (!obj) {
        return "";
    }
    let hash = "";
    if (Array.isArray(obj)) {
        hash += obj.join(HASH_DELIMITER);
    } else if (isObject(obj)) {
        each(values(obj), (val) => {
            hash += HASH_DELIMITER + getObjectHash(val);
        });
    } else {
        hash += obj.toString();
    }

    return hash === HASH_DELIMITER ? "" : hash;
}

/**
 * Generate a hash from state object
 * @param stateObj Object  The state object
 * @param hashProperties Array  Array of properties which should be in the hash
 * @returns String
 */
function getHash(stateObj: IStateObject, hashProperties: string[]): string {
    const defaultProperties = persistentProperties[stateObj.type]?.serialize;
    const properties = Array.isArray(hashProperties) && hashProperties.length ? hashProperties : defaultProperties;
    let hash = "";
    each(properties, (prop) => {
        // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type; string can't index IStateObject
        hash += HASH_DELIMITER + getObjectHash(stateObj[prop]);
    });

    return hash;
}

/**
 * parse an appId
 * "0_com.sq.dragonsworld"
 * "1_5678765986,345345435,"
 * @param appId
 * @returns [number, string, string[]]
 */
function parseAppId(appId?: string): [number, string, string[]] | null {
    if (!appId) {
        return null;
    }
    const parts = appId.match(/([01])_(.*)/);
    // @ts-ignore FIXME: strict migration - 'parts' is possibly 'null'
    const appStoreInt = parseInt(parts[1]);
    // @ts-ignore FIXME: strict migration - 'parts' is possibly 'null'
    const appsArray = parts[2].split(",");
    // @ts-ignore FIXME: strict migration - appsArray.shift() returns string | undefined
    return [appStoreInt, appsArray.shift(), appsArray];
}

/**
 * checks whether serialized state has compared apps or websites
 * @param serializedState
 * @returns {boolean}
 */
function isCompare(serializedState: IStateObject) {
    return Array.isArray(serializedState.comparedItems) && serializedState.comparedItems.length > 0;
}

/* Public */

interface PageSettingsParams {
    appId?: string | number;
    duration?: number;
    country?: number | string;
    key?: string;
    keyword?: string;
    keywords?: string;
    id?: string[];
    searchTerm?: string[];
    keywordGroup?: string[];
    topic?: string[];
    aiTopic?: string[];
    category?: string;
    webSource?: string;
    store?: string;
    device?: string;
    mode?: string;
    industry?: string;
    comparedDuration?: string;
}

interface PageSettings {
    params?: PageSettingsParams;
    state?: any;
}

/**
 * serailzes the current state to a persistent object
 * @returns {*}
 */
async function serializeState(pageSettings: PageSettings = {}): Promise<IStateObject | null> {
    // for favorites and recent
    let serializedObj: IStateObject | null = null;
    const { params = proNavigator.getParams(), state = proNavigator.current() } = pageSettings;

    if (!params) {
        return null;
    }

    // @ts-ignore FIXME: strict migration - Property '#' does not exist on type 'PageSettingsParams | Record<string, any>'
    delete params["#"];

    if (isEmpty(params)) {
        return null;
    }

    switch (true) {
        case proNavigator.isAppAnalysis(state): {
            const parsedAppId = parseAppId(params.appId);
            serializedObj = {
                type: "app",
                // @ts-ignore FIXME: 'parsedAppId' is possibly 'null'
                mainItem: parsedAppId[1],
                // @ts-ignore FIXME: 'parsedAppId' is possibly 'null'
                comparedItems: parsedAppId[2],
                // @ts-ignore FIXME: 'parsedAppId' is possibly 'null'
                appStore: parsedAppId[0],
                duration: params.duration,
                country: parseInt(params.country),
                visits: 1,
            };
            break;
        }
        case proNavigator.isKeywordAnalysis(state): {
            const sharedProperties: IStateObject = {
                type: "keyword",
                mainItem: "",
                duration: params.duration,
                country: parseInt(params.country),
                stateName: state.name,
                pageTitle: state.pageTitle,
                params,
                visits: 1,
            };

            if (params.id?.length) {
                const lastId = params.id[params.id.length - 1] as string;
                const entity = parseKeywordListId(lastId);

                serializedObj = {
                    ...sharedProperties,
                    mainItem: entity?.name || lastId,
                };
            } else if (params.searchTerm?.length) {
                const lastSearchTerm = params.searchTerm[params.searchTerm.length - 1] as string;
                serializedObj = {
                    ...sharedProperties,
                    searchTerm: [lastSearchTerm],
                    mainItem: lastSearchTerm,
                };
            } else if (params.keywordGroup?.length) {
                const lastKeywordGroup = params.keywordGroup[params.keywordGroup.length - 1] as string;

                serializedObj = {
                    ...sharedProperties,
                    keywordGroup: [lastKeywordGroup],
                    mainItem: lastKeywordGroup,
                };
            } else if (params.topic?.length) {
                const lastTopic = params.topic[params.topic.length - 1] as string;
                serializedObj = {
                    ...sharedProperties,
                    topic: [lastTopic],
                    mainItem: lastTopic,
                };
            } else if (params.aiTopic?.length) {
                const lastAiTopic = params.aiTopic[params.aiTopic.length - 1] as string;
                serializedObj = {
                    ...sharedProperties,
                    aiTopic: [lastAiTopic],
                    mainItem: lastAiTopic,
                };
            } else if (params.keyword) {
                let mainItem = params.keyword;
                let category = null;

                if (params.keyword.startsWith(KEYWORD_GROUP_PREFIX)) {
                    try {
                        const groupId = params.keyword.substring(1);
                        const group = await keywordGroupsQueryClient.getGroupById(groupId);
                        mainItem = group?.Name || params.keyword;

                        const { userGroups, sharedGroups } = await keywordGroupsQueryClient.getAllGroups();
                        const allGroups = [...userGroups, ...sharedGroups];
                        const foundGroup = allGroups.find((g) => g.Name === params.keyword);
                        category = foundGroup?.Id || null;
                    } catch (error) {
                        Sentry.error("Error fetching keyword group for serialization", error as Error);
                        mainItem = params.keyword;
                        category = null;
                    }
                }

                serializedObj = {
                    ...sharedProperties,
                    mainItem,
                    // @ts-ignore FIXME: strict migration - Type 'string | null' is not assignable to type 'string | undefined'
                    category,
                    keyword: params.keyword,
                };
            } else if (params.keywords) {
                try {
                    const keywordArray = params.keywords.split(",");
                    const { userGroups, sharedGroups } = await keywordGroupsQueryClient.getAllGroups();
                    const allGroups = [...userGroups, ...sharedGroups];

                    const keywords = await Promise.all(
                        keywordArray.map(async (keyword: string) => {
                            const group = allGroups.find((g) => g.Id === keyword);
                            return group?.Id ? `*${group.Id}` : keyword;
                        }),
                    );

                    const firstKeywordGroup = allGroups.find((g) => g.Id === keywordArray[0]);
                    const mainItem = firstKeywordGroup?.Name ?? keywords[0];

                    serializedObj = {
                        ...sharedProperties,
                        comparedItems: keywords.slice(1),
                        mainItem,
                        keyword: keywords[0],
                    };
                } catch (error) {
                    Sentry.error("Error fetching keyword groups for comparison serialization", error as Error);
                    const keywords = params.keywords.split(",");
                    serializedObj = {
                        ...sharedProperties,
                        comparedItems: keywords.slice(1),
                        mainItem: keywords[0],
                        keyword: keywords[0],
                    };
                }
            }
            break;
        }
        case proNavigator.isIndustryAnalysis(state): {
            const categoryParam = params.category;
            let categoryId;
            let mainItem;

            if (Array.isArray(categoryParam)) {
                const primaryCategory = categoryService.categoryQueryParamToCategoryObject(categoryParam[0]);
                categoryId = primaryCategory?.categoryId;
                mainItem = categoryParam[0];
            } else {
                const category = categoryService.categoryQueryParamToCategoryObject(categoryParam);
                categoryId = category?.categoryId;
                mainItem = categoryParam;
            }

            serializedObj = {
                type: "industry",
                category: categoryId,
                mainItem: mainItem,
                duration: params.duration,
                country: parseInt(params.country),
                webSource: params.webSource || "Desktop",
                visits: 1,
            };
            break;
        }
        case proNavigator.isAppCategory(state): {
            serializedObj = {
                type: "appCategory",
                store: params.store,
                country: parseInt(params.country),
                category: params.category,
                device: params.device,
                mode: params.mode,
                mainItem: params.category,
                visits: 1,
            };
            break;
        }
        case proNavigator.isIndustryConversion(state): {
            serializedObj = {
                type: "conversionIndustry",
                country: parseInt(params.country),
                industry: params.industry, // parent
                category: params.category, // child
                duration: params.duration,
                mainItem: params.industry,
                visits: 1,
            };
            break;
        }
        case proNavigator.isLeadGenerator(state): {
            serializedObj = {
                type: "conversionIndustry",
                country: parseInt(params.country),
                industry: params.industry, // parent
                category: params.category, // child
                duration: params.duration,
                mainItem: params.industry,
                visits: 1,
            };
            break;
        }
        case proNavigator.isSalesCompanyAnalysis(state): {
            serializedObj = {
                type: "company",
                mainItem: params.key.split(",")[0],
                stateName: state.name,
                pageTitle: state.pageTitle,
                params,
                visits: 1,
            };
            break;
        }
        default: {
            if (!params.key || proNavigator.isLeadGenerator(state)) {
                serializedObj = null;
                break;
            }

            const siteParts: string[] = params.key.split(",");
            const mainSite: string = siteParts[0];
            const isVirtual = mainSite[0] === "*";

            serializedObj = {
                type: isVirtual ? "virtual" : "website",
                mainItem: isVirtual ? mainSite.substr(1) : mainSite,
                comparedItems: tail(siteParts),
                duration: params.duration,
                country: parseInt(params.country),
                comparedDuration: params.comparedDuration,
                stateName: state.name,
                pageTitle: state.pageTitle,
                params,
                visits: 1,
            };
            break;
        }
    }

    // The serialized object did not match any of the states and stayed null
    // Return null
    if (!serializedObj) {
        return null;
    }

    serializedObj.pageId = state ? state.pageId : proNavigator.getCurrentPageId();
    serializedObj.hideFromRecents = state ? state.hideFromRecents : false;

    if (state && state.origin) {
        serializedObj.origin = state.origin;
    }

    return serializedObj;
}

function toNavigationParams(serializedState: IStateObject): any {
    const comparedStr = isCompare(serializedState) ? "," + serializedState.comparedItems?.join() : "";

    switch (serializedState.type) {
        case "website":
            return {
                key: serializedState.mainItem + comparedStr,
                isWWW: "*",
                country: serializedState.country,
                duration: serializedState.duration,
                comparedDuration: serializedState.comparedDuration,
            };

        case "virtual":
            return {
                key: "*" + serializedState.mainItem + comparedStr,
                isWWW: "*",
                country: serializedState.country,
                duration: serializedState.duration,
            };

        case "app":
            return {
                appId: serializedState.appStore + "_" + serializedState.mainItem + comparedStr,
                country: serializedState.country,
                duration: serializedState.duration,
                keyword: serializedState.keyword,
            };

        case "keyword": {
            const { country, duration, params } = serializedState;

            if (params?.id?.length) {
                return { id: params.id, country, duration };
            }

            const priorities = ["searchTerm", "keywordGroup", "topic", "keyword"] as const;

            for (const key of priorities) {
                const value = serializedState[key];
                if (value && (Array.isArray(value) ? value.length > 0 : value)) {
                    return { [key]: value, country, duration };
                }
            }

            return { country, duration };
        }
        case "industry":
            return {
                category: serializedState.mainItem,
                country: serializedState.country,
                duration: serializedState.duration,
                webSource: serializedState.webSource,
            };
        case "appCategory":
            return {
                store: serializedState.store,
                country: serializedState.country,
                category: serializedState.category,
                device: serializedState.device,
                mode: serializedState.mode,
            };
        default:
            return {};
    }
}

/**
 * deserialize a state object to a URL
 * @param serializedState
 * @returns string url
 */
function deSerializeState(serializedState: IStateObject) {
    const pageId = serializedState.pageId;
    if (!pageId) {
        return "";
    }

    const params = toNavigationParams(serializedState);

    const allStates = proNavigator.getAllStates();
    const state: any = find(allStates, {
        section: pageId.section,
        subSection: pageId.subSection,
        subSubSection: pageId.subSubSection,
    });
    // RIPNG: liorb - remove this default
    if (!state) {
        return "";
    }
    return proNavigator.getStateUrl(state.name, Object.assign(params, state.defaultQueryParams));
}

function applyStateDefaults(stateObj: IStateObject): IStateObject {
    let defaultParams = {};

    if (stateObj.pageId) {
        const navState = proNavigator.getStateForPageId(stateObj.pageId);
        const component = proNavigator.getConfigId(navState as IRouterState);
        defaultParams =
            component && swSettings.components[component] ? swSettings.components[component].defaultParams : {};
    }
    return Object.assign({}, defaultGlobalState[stateObj.type], defaultParams, stateObj);
}

function applyDefaults<T extends IBaseDataObject>(favoritesOrRecentObj: T[]): T[] {
    each(favoritesOrRecentObj, (obj: T, index: number) => {
        favoritesOrRecentObj[index].data = applyStateDefaults(obj.data);
    });
    return favoritesOrRecentObj || [];
}

function isEqual(stateObj1: IStateObject, stateObj2: IStateObject): boolean {
    if (stateObj1.type !== stateObj2.type) {
        return false;
    }
    const params: string[] | undefined = persistentProperties[stateObj1.type]?.serialize;

    return !some(params, function (param: string) {
        // @ts-ignore FIXME: strict migration - expression of type 'string' can't be used to index type 'IStateObject'
        let val1 = stateObj1[param],
            // @ts-ignore FIXME: strict migration - expression of type 'string' can't be used to index type 'IStateObject'
            val2 = stateObj2[param];
        if (param === "appStore") {
            [val1, val2] = [val1, val2].map((val) => (/^\d$/.test(val) ? +val : /google/i.test(val) ? 0 : 1));
        }
        return !isEqual(val1, val2);
    });
}

function getUniques(items: IBaseDataObject[], hashProperties: string[]) {
    const hashMap: { [key: string]: number } = {};
    const uniques: IBaseDataObject[] = [];
    each(items, (item: IBaseDataObject) => {
        const hash = getHash(item.data, hashProperties),
            existingIndex = hashMap[hash];
        if (!isUndefined(existingIndex)) {
            if (
                (<IFavoriteObject>item).addedTime > (<IFavoriteObject>uniques[existingIndex]).addedTime ||
                (<IRecentObject>item).updatedTime > (<IRecentObject>uniques[existingIndex]).updatedTime
            ) {
                uniques[existingIndex] = item;
            }
        } else {
            uniques.push(item);
            hashMap[hash] = uniques.length - 1;
        }
    });

    return uniques;
}

//mainItem - 'websites'/'virtual' - item.name; 'app' - item.id
function buildItemUrl(
    itemType: StateObjectType,
    mainItem: string,
    comparedItems?: string[],
    itemStore?: AppStoreStr,
    pageId?: PageId,
) {
    const newPageId: PageId = pageId
        ? pageId
        : itemType === "app"
          ? PAGE_ID_DEFAULTS.appDefaultPageId[0].pageId
          : PAGE_ID_DEFAULTS.websiteDefaultPageId[0].pageId;

    const defaults: IStateObject = {
        type: itemType,
        mainItem: mainItem,
        comparedItems: comparedItems,
        pageId: newPageId,
        visits: 1,
    };

    if (itemStore) {
        defaults.appStore = itemStore == "google" ? AppStore.Google : AppStore.Apple;
    }

    const stateObject = applyStateDefaults(defaults);
    return deSerializeState(stateObject);
}

export const stateService = {
    serializeState,
    deSerializeState,
    applyDefaults,
    applyStateDefaults,
    toNavigationParams,
    buildItemUrl,
    getUniques,
    isEqual,
};
